import React, { useState, useRef } from 'react'
import {
  Box,
  Stack,
  Switch,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material'
import { VariableAllocationTable, VariableAllocationRow } from 'types/comp-allocations'
import Tooltip from 'components/@extended/Tooltip'
import IconButton from 'components/@extended/IconButton'
import { DeleteOutlined, EditOutlined, PlusOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { Period, findPeriodById } from 'types/periods'
import { RowType } from 'types/comp-allocations'
import EditAllocationRowDialog from './EditAllocationRowDialog'
import PercentageRangeInput from 'components/PercentageRangeInput'

interface TemplateComponentsSectionTableProps {
  table: VariableAllocationTable
  updateTable: (table: VariableAllocationTable) => void
  deleteTable: () => void
  selectedOrganizations: string[]
  period: Period | null
  organizationMap: Record<string, string>
}

const formatRowType = (value: string): string => {
  switch (value) {
    case RowType.DISPLAY_ROW:
      return 'Display Only'
    case RowType.CALCULATION_ROW:
      return 'Variable'
    case RowType.FIXED_VALUE_ROW:
      return 'Fixed Value'
    default:
      return ''
  }
}

const TemplateComponentsSectionTable: React.FC<TemplateComponentsSectionTableProps> = ({
  table,
  updateTable,
  deleteTable,
  selectedOrganizations,
  period,
  organizationMap,
}) => {
  const theme = useTheme()
  const [editSectionState, setEditSectionState] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<VariableAllocationRow | null>(null)
  const [editedSectionLabel, setEditedSectionLabel] = useState<string>('')
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false)
  const [allocationEditRow, setAllocationEditRow] = useState<VariableAllocationRow | null>(null)
  const [allocationValue, setAllocationValue] = useState<number>(0)
  const [minAllocationValue, setMinAllocationValue] = useState<number>(0)
  const [maxAllocationValue, setMaxAllocationValue] = useState<number>(0)
  // Delete confirmation state
  const [deleteRowDialogOpen, setDeleteRowDialogOpen] = useState(false)
  const [rowToDelete, setRowToDelete] = useState<VariableAllocationRow | null>(null)
  const [deleteSectionDialogOpen, setDeleteSectionDialogOpen] = useState(false)

  // Payout quick-edit dialog state
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false)
  const [payoutEditRow, setPayoutEditRow] = useState<VariableAllocationRow | null>(null)
  const [payoutValue, setPayoutValue] = useState<number>(0)
  const [minPayoutValue, setMinPayoutValue] = useState<number>(0)
  const [maxPayoutValue, setMaxPayoutValue] = useState<number>(0)

  const wasSavedRef = useRef(false)

  const handleEditRow = (row: VariableAllocationRow) => {
    setSelectedRow(row)
    setEditDialogOpen(true)
  }

  const handleDeleteRowClick = (row: VariableAllocationRow) => {
    setRowToDelete(row)
    setDeleteRowDialogOpen(true)
  }

  const confirmDeleteRow = () => {
    if (rowToDelete) {
      const updatedRows = table.rows.filter((r) => r.templateAllocationRowId !== rowToDelete.templateAllocationRowId)
      const updatedTable = { ...table, rows: updatedRows }
      updateTable(updatedTable)
    }
    setDeleteRowDialogOpen(false)
    setRowToDelete(null)
  }

  const handleDeleteSectionClick = () => {
    setDeleteSectionDialogOpen(true)
  }

  const confirmDeleteSection = () => {
    deleteTable()
    setDeleteSectionDialogOpen(false)
  }

  const handleRestrictedVisibilityToggle = (row: VariableAllocationRow) => {
    const updatedRow = { ...row, restrictedVisibility: !row.restrictedVisibility }
    updateTable({
      ...table,
      rows: table.rows.map((r) => (r.templateAllocationRowId === row.templateAllocationRowId ? updatedRow : r)),
    })
  }

  const handlePayoutClick = (row: VariableAllocationRow) => {
    setPayoutEditRow(row)
    setPayoutValue(row.defaultPayout || 1.0)
    setMinPayoutValue(row.minPayout || 1.0)
    setMaxPayoutValue(row.maxPayout || 1.5)
    setPayoutDialogOpen(true)
  }

  const handlePayoutSave = () => {
    if (payoutEditRow) {
      const updatedRow = {
        ...payoutEditRow,
        defaultPayout: payoutValue,
        minPayout: minPayoutValue,
        maxPayout: maxPayoutValue,
      }
      const updatedRows = table.rows.map((row) =>
        row.templateAllocationRowId === payoutEditRow.templateAllocationRowId ? updatedRow : row
      )
      updateTable({ ...table, rows: updatedRows })
    }
    setPayoutDialogOpen(false)
    setPayoutEditRow(null)
  }

  const handlePayoutCancel = () => {
    setPayoutDialogOpen(false)
    setPayoutEditRow(null)
  }

  const handleNewRow = () => {
    const newRow: VariableAllocationRow = {
      templateAllocationRowId: 'new-' + crypto.randomUUID(),
      label: '',
      rowType: RowType.CALCULATION_ROW,
      restrictedVisibility: false,
      periodId: period?.id || '',
      allocationPercentage: 0.25,
      minAllocationPercentage: 0.1,
      maxAllocationPercentage: 0.5,
      defaultPayout: 1.25,
      minPayout: 1.1,
      maxPayout: 1.5,
      formulaRequirements: [],
      formulaOptions: [],
      achievementMultiplier: '1.0',
    }

    // Add the new row to the table immediately
    const updatedTable = { ...table, rows: [...table.rows, newRow] }
    updateTable(updatedTable)

    // Then open the dialog for editing
    setSelectedRow(newRow)
    setEditDialogOpen(true)
  }

  const handleCloseDialog = () => {
    // If we're closing the dialog for a new row that wasn't saved, remove it
    if (
      !wasSavedRef.current &&
      selectedRow &&
      selectedRow.templateAllocationRowId?.startsWith('new-') &&
      selectedRow.label === ''
    ) {
      const updatedRows = table.rows.filter((r) => r.templateAllocationRowId !== selectedRow.templateAllocationRowId)
      const updatedTable = { ...table, rows: updatedRows }
      updateTable(updatedTable)
    }

    setEditDialogOpen(false)
    setSelectedRow(null)
  }

  const handleAllocationClick = (row: VariableAllocationRow) => {
    setAllocationEditRow(row)
    setAllocationValue(row.allocationPercentage || 0.2)
    setMinAllocationValue(row.minAllocationPercentage || 0.2)
    setMaxAllocationValue(row.maxAllocationPercentage || 0.2)
    setAllocationDialogOpen(true)
  }

  const handleAllocationSave = () => {
    if (allocationEditRow) {
      const updatedRow = {
        ...allocationEditRow,
        allocationPercentage: allocationValue,
        minAllocationPercentage: minAllocationValue,
        maxAllocationPercentage: maxAllocationValue,
      }
      const updatedRows = table.rows.map((row) =>
        row.templateAllocationRowId === allocationEditRow.templateAllocationRowId ? updatedRow : row
      )
      const updatedTable = { ...table, rows: updatedRows }
      updateTable(updatedTable)
    }
    setAllocationDialogOpen(false)
    setAllocationEditRow(null)
    setAllocationValue(0)
    setMinAllocationValue(0)
    setMaxAllocationValue(0)
  }

  const handleAllocationCancel = () => {
    setAllocationDialogOpen(false)
    setAllocationEditRow(null)
    setAllocationValue(0)
    setMinAllocationValue(0)
    setMaxAllocationValue(0)
  }

  const handleSaveRow = (updatedRow: VariableAllocationRow) => {
    wasSavedRef.current = true

    if (updatedRow.templateAllocationRowId?.startsWith('new-')) {
      // For new rows, update the existing temporary row and give it a permanent ID
      const permanentRow = { ...updatedRow, templateAllocationRowId: crypto.randomUUID() }
      const updatedRows = table.rows.map((row) =>
        row.templateAllocationRowId === updatedRow.templateAllocationRowId ? permanentRow : row
      )
      const updatedTable = { ...table, rows: updatedRows }
      updateTable(updatedTable)
    } else {
      // Update existing row
      const updatedRows = table.rows.map((row) =>
        row.templateAllocationRowId === updatedRow.templateAllocationRowId ? updatedRow : row
      )
      const updatedTable = { ...table, rows: updatedRows }
      updateTable(updatedTable)
    }

    setSelectedRow(null)
  }

  return (
    <Box border={2} borderColor="grey.300" borderRadius={2}>
      <Stack direction="column" spacing={0} sx={{ p: 1 }}>
        <Stack
          id="edit-section-control"
          direction="row"
          alignItems="stretch"
          spacing={1}
          justifyContent="space-between"
        >
          {editSectionState ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                sx={{ width: 150 }}
                value={editedSectionLabel}
                onChange={(e) => setEditedSectionLabel(e.target.value)}
              />
              <Tooltip title="Edit Section Label">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditSectionState(false)
                    updateTable({ ...table, label: editedSectionLabel })
                  }}
                >
                  <CheckCircleOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography width="150" variant="h5">
                {table.label}
              </Typography>
              <Tooltip title="Edit Section Label">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditSectionState(true)
                    setEditedSectionLabel(table.label)
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
          <Tooltip title="Delete Section">
            <IconButton size="small" onClick={handleDeleteSectionClick}>
              <DeleteOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
        <Box sx={{ width: '100%' }}>
          <TableContainer>
            <Table stickyHeader size="small" aria-label="allocation table">
              <TableHead
                sx={{
                  '& th': {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider} !important`,
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ minWidth: 150 }}>Label</TableCell>
                  <TableCell sx={{ minWidth: 90 }}>Type</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Period</TableCell>
                  <TableCell sx={{ minWidth: 100 }} align="center">
                    Allocation %
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }} align="center">
                    Max Payout %
                  </TableCell>
                  <TableCell sx={{ minWidth: 80 }} align="center">
                    Restricted
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }} align="center">
                    Accelerator
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {table.rows.length > 0 ? (
                  table.rows.map((row) => {
                    const periodLabel = row.periodId ? findPeriodById([period!], row.periodId)?.label || '' : ''
                    const allocationDisplay =
                      row.allocationPercentage != null ? `${row.allocationPercentage * 100}%` : ''
                    const maxPayoutDisplay = row.maxPayout != null ? `${row.maxPayout * 100}%` : ''
                    const acceleratorRaw = row.achievementMultiplier || '0'
                    const numericAccelerator = parseFloat(acceleratorRaw.replace(/[^\d.-]/g, ''))
                    const acceleratorDisplay = isNaN(numericAccelerator)
                      ? '0.00'
                      : (Math.round(numericAccelerator * 100) / 100).toFixed(2)

                    return (
                      <TableRow key={row.templateAllocationRowId} sx={{ py: 3 }} hover>
                        <TableCell>{row.label}</TableCell>
                        <TableCell>{formatRowType(row.rowType || '')}</TableCell>
                        <TableCell>{periodLabel}</TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                            onClick={() => handleAllocationClick(row)}
                          >
                            {allocationDisplay}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                            onClick={() => handlePayoutClick(row)}
                          >
                            {maxPayoutDisplay}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={!!row.restrictedVisibility}
                            onChange={() => handleRestrictedVisibilityToggle(row)}
                          />
                        </TableCell>
                        <TableCell align="center">{acceleratorDisplay}</TableCell>
                        <TableCell align="center">
                          <Box display="flex" justifyContent="center">
                            <Tooltip title="Edit Component" placement="top">
                              <IconButton size="small" onClick={() => handleEditRow(row)}>
                                <EditOutlined />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Component" placement="top">
                              <IconButton size="small" onClick={() => handleDeleteRowClick(row)}>
                                <DeleteOutlined />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No allocation rows yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ pt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Add Allocation Row">
              <IconButton variant="outlined" size="small" onClick={handleNewRow}>
                <PlusOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <EditAllocationRowDialog
          open={editDialogOpen}
          onClose={handleCloseDialog}
          onSave={handleSaveRow}
          row={selectedRow}
          selectedOrganizations={selectedOrganizations}
          organizationMap={organizationMap}
          period={period}
        />

        {/* Row Delete Confirmation Dialog */}
        <Dialog open={deleteRowDialogOpen} onClose={() => setDeleteRowDialogOpen(false)}>
          <DialogTitle>Delete Row</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete {rowToDelete?.label ? `"${rowToDelete.label}"` : 'this row'}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteRowDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={confirmDeleteRow} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Section Delete Confirmation Dialog */}
        <Dialog open={deleteSectionDialogOpen} onClose={() => setDeleteSectionDialogOpen(false)}>
          <DialogTitle>Delete Section</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the section &quot;{table.label}&quot; and all its rows?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteSectionDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={confirmDeleteSection} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payout Quick-Edit Dialog */}
        <Dialog open={payoutDialogOpen} onClose={handlePayoutCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Payout Percentage</DialogTitle>
          <DialogContent>
            <PercentageRangeInput
              label="Payout Percentage *"
              defaultValue={payoutValue}
              setDefaultValue={setPayoutValue}
              minValue={minPayoutValue}
              setMinValue={setMinPayoutValue}
              maxValue={maxPayoutValue}
              setMaxValue={setMaxPayoutValue}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePayoutCancel} color="inherit">
              Cancel
            </Button>
            <Button onClick={handlePayoutSave} variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={allocationDialogOpen} onClose={handleAllocationCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Allocation Percentage</DialogTitle>
          <DialogContent>
            <PercentageRangeInput
              label="Allocation Percentage *"
              defaultValue={allocationValue}
              setDefaultValue={setAllocationValue}
              minValue={minAllocationValue}
              setMinValue={setMinAllocationValue}
              maxValue={maxAllocationValue}
              setMaxValue={setMaxAllocationValue}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAllocationCancel} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleAllocationSave} variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  )
}

export default TemplateComponentsSectionTable
